import { getApi } from "../apis/api";
import { GET_AGENT_NUMBER } from "../apis/end-points";

/** `GET_AGENT_NUMBER` response payload. */
interface AgentNumberResponse {
  agentId: string;
  agentNumber: string;
}

/**
 * An agent number never changes, so it is cached for the lifetime of the tab.
 * The cache lives at module level — not inside a hook — so the agent tabs badge,
 * every download button and every remounted tab share a single lookup per agent.
 * Keyed by agent id, so switching agents never reuses the previous one's number.
 */
const resolvedNumbers = new Map<string, string>();
/** Concurrent lookups for the same agent share one request. */
const pendingLookups = new Map<string, Promise<string>>();

/** Number already known for this agent, or "" when it still has to be fetched. */
export const getCachedAgentNumber = (agentId?: string): string =>
  (agentId && resolvedNumbers.get(agentId)) || "";

/**
 * Publishes a number a screen already had (list row, redux, navigation state) so
 * the rest of the app can skip the lookup. Callers must be sure it belongs to
 * `agentId` — a mismatched value would follow the agent everywhere.
 */
export const cacheAgentNumber = (
  agentId?: string,
  agentNumber?: string
): void => {
  if (agentId && agentNumber) resolvedNumbers.set(agentId, agentNumber);
};

/**
 * Resolves an agent number, hitting the API only when it is not already known.
 * Resolves to "" when it cannot be resolved — failures are not cached, so the
 * next caller retries.
 */
export const resolveAgentNumber = (agentId?: string): Promise<string> => {
  if (!agentId) return Promise.resolve("");

  const cached = resolvedNumbers.get(agentId);
  if (cached) return Promise.resolve(cached);

  const pending = pendingLookups.get(agentId);
  if (pending) return pending;

  const lookup = getApi<AgentNumberResponse>(`${GET_AGENT_NUMBER}${agentId}`)
    .then((response) => {
      const agentNumber = response?.data?.agentNumber || "";
      cacheAgentNumber(agentId, agentNumber);
      return agentNumber;
    })
    .catch(() => "")
    .finally(() => {
      pendingLookups.delete(agentId);
    });

  pendingLookups.set(agentId, lookup);
  return lookup;
};
